import { useCallback, useEffect, useMemo, useState } from 'react';
import {
  type MeResponse,
  type SectionPermission,
  type SessionChangedResponse,
  type SessionStatusResponse,
  type SupportedLocale,
} from '@stm/contracts';
import { ApiRequestError, apiRequest } from './api';

export type AccountState =
  | { status: 'loading' }
  | { status: 'anonymous' }
  /** The API could not be reached or answered unexpectedly. */
  | { status: 'unavailable' }
  | { status: 'signedIn'; me: MeResponse; csrfToken: string };

export interface AccountActions {
  selectOrganization: (organizationId: string) => Promise<void>;
  acceptInvitation: (token: string) => Promise<void>;
  signOut: () => Promise<void>;
  /** Reloads `/me` without leaving the page, for example after the club logo changed. */
  refreshAccount: () => Promise<void>;
  /** Remembers the language for the active organization; failure is harmless and ignored. */
  rememberLocale: (locale: SupportedLocale) => void;
}

/** Sections the signed-in person may open in the active organization. */
export function grantedSections(account: AccountState): ReadonlySet<SectionPermission> {
  return account.status === 'signedIn' ? new Set(account.me.sections) : new Set();
}

/** Where `/` leads: the active organization's home, the chooser, or nowhere yet. */
export type Landing =
  | { kind: 'wait' }
  | { kind: 'login' }
  | { kind: 'home'; organizationId: string }
  | { kind: 'choose' }
  | { kind: 'noAccess' }
  | { kind: 'unavailable' };

export function landingFor(account: AccountState): Landing {
  switch (account.status) {
    case 'loading':
      return { kind: 'wait' };
    case 'anonymous':
      return { kind: 'login' };
    case 'unavailable':
      return { kind: 'unavailable' };
    case 'signedIn': {
      const { me } = account;
      if (me.activeOrganizationId !== null) {
        return { kind: 'home', organizationId: me.activeOrganizationId };
      }
      return me.memberships.length === 0 ? { kind: 'noAccess' } : { kind: 'choose' };
    }
  }
}

async function loadAccount(): Promise<AccountState> {
  try {
    const session = await apiRequest<SessionStatusResponse>('/api/v1/auth/session');
    if (!session.authenticated || session.csrfToken === undefined) return { status: 'anonymous' };
    const me = await apiRequest<MeResponse>('/api/v1/me');
    return { status: 'signedIn', me, csrfToken: session.csrfToken };
  } catch (error) {
    if (error instanceof ApiRequestError && error.status === 401) return { status: 'anonymous' };
    return { status: 'unavailable' };
  }
}

/**
 * Loads the session and `/me`, and exposes the account actions. Switching organization or accepting
 * an invitation rotates the server session, so the CSRF token and `/me` are reloaded afterwards;
 * nothing tenant-specific is kept in the browser beyond this state.
 */
export function useAccount(): { account: AccountState; actions: AccountActions } {
  const [account, setAccount] = useState<AccountState>({ status: 'loading' });

  useEffect(() => {
    let cancelled = false;
    void loadAccount().then((loaded) => {
      if (!cancelled) setAccount(loaded);
    });
    return () => {
      cancelled = true;
    };
  }, []);

  const csrfToken = account.status === 'signedIn' ? account.csrfToken : undefined;
  const activeOrganizationId =
    account.status === 'signedIn' ? account.me.activeOrganizationId : null;

  const reload = useCallback(async () => {
    setAccount(await loadAccount());
  }, []);

  const actions = useMemo<AccountActions>(
    () => ({
      selectOrganization: async (organizationId) => {
        await apiRequest<SessionChangedResponse>('/api/v1/me/organization', {
          method: 'POST',
          body: { organizationId },
          csrfToken,
        });
        await reload();
      },
      acceptInvitation: async (token) => {
        await apiRequest<SessionChangedResponse>('/api/v1/invitations/accept', {
          method: 'POST',
          body: { token },
          csrfToken,
        });
        await reload();
      },
      refreshAccount: reload,
      signOut: async () => {
        let logoutUrl: string | null = null;
        try {
          const result = await apiRequest<{ logoutUrl: string | null }>('/api/v1/auth/logout', {
            method: 'POST',
            csrfToken,
          });
          logoutUrl = result.logoutUrl;
        } catch {
          // The session cookie is cleared by the server even when the provider logout fails.
        }
        window.location.assign(logoutUrl ?? '/login');
      },
      rememberLocale: (locale) => {
        if (activeOrganizationId === null) return;
        void apiRequest(`/api/v1/organizations/${activeOrganizationId}/me/locale`, {
          method: 'PUT',
          body: { locale },
          csrfToken,
        }).catch(() => undefined);
      },
    }),
    [activeOrganizationId, csrfToken, reload],
  );

  return { account, actions };
}
